<template>
  <div :class="containerClasses">
    <div :class="spinnerClasses">
      <!-- Spinner SVG -->
      <svg
        v-if="variant === 'spinner'"
        class="animate-spin"
        :class="iconSizeClasses"
        fill="none"
        viewBox="0 0 24 24"
      >
        <circle
          class="opacity-25"
          cx="12"
          cy="12"
          r="10"
          stroke="currentColor"
          stroke-width="4"
        />
        <path
          class="opacity-75"
          fill="currentColor"
          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
        />
      </svg>

      <!-- Dots Loading -->
      <div v-else-if="variant === 'dots'" class="flex space-x-1">
        <div
          v-for="i in 3"
          :key="i"
          :class="[dotSizeClasses, colorClasses]"
          class="rounded-full animate-pulse"
          :style="{ animationDelay: `${(i - 1) * 0.2}s` }"
        />
      </div>

      <!-- Pulse Loading -->
      <div
        v-else-if="variant === 'pulse'"
        :class="[pulseSizeClasses, colorClasses]"
        class="rounded-full animate-pulse"
      />

      <!-- Bar Loading -->
      <div v-else-if="variant === 'bars'" class="flex space-x-1">
        <div
          v-for="i in 4"
          :key="i"
          :class="[barSizeClasses, colorClasses]"
          class="animate-pulse"
          :style="{ animationDelay: `${(i - 1) * 0.15}s` }"
        />
      </div>
    </div>

    <!-- Loading Text -->
    <p v-if="text" :class="textClasses">
      {{ text }}
    </p>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'

type LoadingVariant = 'spinner' | 'dots' | 'pulse' | 'bars'
type LoadingSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl'
type LoadingColor = 'primary' | 'secondary' | 'white' | 'gray'

const props = withDefaults(defineProps<{
  variant?: LoadingVariant
  size?: LoadingSize
  color?: LoadingColor
  text?: string
  center?: boolean
  overlay?: boolean
}>(), {
  variant: 'spinner',
  size: 'md',
  color: 'primary',
  text: '',
  center: false,
  overlay: false
})

const containerClasses = computed(() => [
  'flex items-center',
  props.center ? 'justify-center' : '',
  props.text ? 'flex-col space-y-2' : '',
  props.overlay ? 'fixed inset-0 bg-white bg-opacity-75 z-50' : '',
  props.center && !props.text ? 'space-x-2' : '',
  props.center && props.text ? 'py-8' : ''
])

const spinnerClasses = computed(() => [
  'flex items-center justify-center',
  props.variant === 'dots' || props.variant === 'bars' ? '' : colorClasses.value
])

const iconSizeClasses = computed(() => {
  switch (props.size) {
    case 'xs': return 'w-3 h-3'
    case 'sm': return 'w-4 h-4'
    case 'md': return 'w-6 h-6'
    case 'lg': return 'w-8 h-8'
    case 'xl': return 'w-12 h-12'
    default: return 'w-6 h-6'
  }
})

const dotSizeClasses = computed(() => {
  switch (props.size) {
    case 'xs': return 'w-1 h-1'
    case 'sm': return 'w-1.5 h-1.5'
    case 'md': return 'w-2 h-2'
    case 'lg': return 'w-3 h-3'
    case 'xl': return 'w-4 h-4'
    default: return 'w-2 h-2'
  }
})

const pulseSizeClasses = computed(() => {
  switch (props.size) {
    case 'xs': return 'w-3 h-3'
    case 'sm': return 'w-4 h-4'
    case 'md': return 'w-6 h-6'
    case 'lg': return 'w-8 h-8'
    case 'xl': return 'w-12 h-12'
    default: return 'w-6 h-6'
  }
})

const barSizeClasses = computed(() => {
  switch (props.size) {
    case 'xs': return 'w-0.5 h-3'
    case 'sm': return 'w-1 h-4'
    case 'md': return 'w-1 h-6'
    case 'lg': return 'w-1.5 h-8'
    case 'xl': return 'w-2 h-12'
    default: return 'w-1 h-6'
  }
})

const colorClasses = computed(() => {
  switch (props.color) {
    case 'primary': return 'text-pdca-600'
    case 'secondary': return 'text-gray-600'
    case 'white': return 'text-white'
    case 'gray': return 'text-gray-400'
    default: return 'text-pdca-600'
  }
})

const textClasses = computed(() => [
  'text-sm font-medium',
  colorClasses.value === 'text-white' ? 'text-white' : 'text-gray-600'
])
</script>

<style scoped>
@keyframes pulse {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
}

.animate-pulse {
  animation: pulse 1.5s ease-in-out infinite;
}
</style>
